import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import { FamilyProvider } from './context/FamilyContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import PublicRoute from './components/auth/PublicRoute';
import AppLayout from './components/layout/AppLayout';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import JoinFamily from './components/onboarding/JoinFamily';
import InviteHandler from './components/invite/InviteHandler';
import OnboardingPage from './pages/OnboardingPage';
import LandingPage from './pages/LandingPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import Dashboard from './pages/Dashboard';
import CalendarPage from './pages/CalendarPage';
import ExpensesPage from './pages/ExpensesPage';
import ExpenseInsightsPage from './pages/ExpenseInsightsPage';
import ActivitiesPage from './pages/ActivitiesPage';
import MessagesPage from './pages/MessagesPage';
import SettingsPage from './pages/SettingsPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import CustodySetup from './components/custody/CustodySetup';
import PaymentHistoryPage from './pages/PaymentHistoryPage';
import SwapsPage from './pages/SwapsPage';
import DocumentsPage from './pages/DocumentsPage';
import ChoresPage from './pages/ChoresPage';
import ChoreHistoryPage from './pages/ChoreHistoryPage';
import HouseholdRulesPage from './pages/HouseholdRulesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import AdminMetricsPage from './pages/AdminMetricsPage';
import AdminSocialEntryPage from './pages/AdminSocialEntryPage';
import usePageviewPing from './hooks/usePageviewPing';

// Needs to live inside <Router> for useLocation().
function PageviewPing() {
  usePageviewPing();
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <Router>
      <AuthProvider>
        <AdminProvider>
        <FamilyProvider>
          <NotificationProvider>
          <PageviewPing />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                fontFamily: '"DM Sans", system-ui, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                borderRadius: '0.75rem',
                padding: '0.625rem 1rem',
                boxShadow: '0 10px 15px -3px rgba(28, 25, 23, 0.06), 0 4px 6px -4px rgba(28, 25, 23, 0.04)',
              },
            }}
          />
          <Routes>
            {/* Landing page: shows marketing site if not logged in, redirects to dashboard if logged in */}
            <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />

            {/* Legal pages: always accessible */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />

            {/* Blog pages: always accessible */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Mid-funnel marketing pages. Public and always accessible — unlike
                the landing page these are NOT wrapped in PublicRoute, because a
                signed-in user following a search result to /pricing should see
                the page, not get bounced to the dashboard. */}
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Invite deep-link: handles both auth and unauth users */}
            <Route path="/invite/:code" element={<InviteHandler />} />

            {/* Auth routes: redirect to dashboard if already logged in */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<Register />} />

            {/* Onboarding routes */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/join"
              element={
                <ProtectedRoute>
                  <JoinFamily />
                </ProtectedRoute>
              }
            />

            {/* App routes with layout */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/expenses" element={<ExpensesPage />} />
              <Route path="/insights" element={<ExpenseInsightsPage />} />
              <Route path="/activities" element={<ActivitiesPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/delete-account" element={<DeleteAccountPage />} />
              <Route path="/custody/setup" element={<CustodySetup />} />
              <Route path="/payments" element={<PaymentHistoryPage />} />
              <Route path="/swaps" element={<SwapsPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/chores" element={<ChoresPage />} />
              <Route path="/chore-history" element={<ChoreHistoryPage />} />
              <Route path="/rules" element={<HouseholdRulesPage />} />
              <Route
                path="/admin/metrics"
                element={
                  <AdminRoute>
                    <AdminMetricsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/social-metrics"
                element={
                  <AdminRoute>
                    <AdminSocialEntryPage />
                  </AdminRoute>
                }
              />
            </Route>
          </Routes>
          </NotificationProvider>
        </FamilyProvider>
        </AdminProvider>
      </AuthProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;
